import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    CircularProgress,
    Alert,
    TableCellProps,
    Tooltip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { httpsCallable } from 'firebase/functions';
import { firebase_functions } from '../../config/firebaseConfig';

dayjs.extend(utc);

interface CallableResponse<T> {
    data: T;
}

interface MembershipReportResult {
    data: MembershipReportData;
    payedByCompanyCount: number;
}

interface ActivityReportResult {
     data: ActivityReportData;
}

interface PlanData { qty: number; revenue?: number; }
interface MembershipTierData { monthly?: PlanData; yearly?: PlanData; }
interface SimpleTotalData { total: number; }
interface ActivityReportData {
    newMemberships: Record<string, MembershipTierData>;
    canceledMemberships: Record<string, MembershipTierData>;
    oneTimePurchases: Record<string, SimpleTotalData>;
    refunds: Record<string, SimpleTotalData>;
}
interface MembershipReportData { [key: string]: MembershipTierData; }

type ReportTableColumn = {
    header: string;
    align?: TableCellProps['align'];
    render: (key: string, itemValue: any) => React.ReactNode;
    headerSubtext?: string;
};

const getSalesActivityReportFunc = httpsCallable(firebase_functions, 'getSalesActivityReport');
const getCurrentMembershipReportFunc = httpsCallable(firebase_functions, 'getCurrentMembershipReport');


const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, })}`;
};

function formatKeyToLabel(key: string): string {
    switch (key) {
        case 'starterPlus': return 'Starter Plus';
        case 'proPlus': return 'Pro Plus';
        case 'addOns': return 'Add-Ons';
        case 'freeTrials': return 'Free Trials';
        case 'trainingCoaching': return 'Training & Coaching';
        case 'oneTimePurchases': return 'One-time Purchases';
        case 'memberships': return 'Memberships';
        case 'basic': return 'Basic';
        case 'pro': return 'Pro';
        case 'refunds': return 'Refunds';
        case 'canceledMemberships': return 'Canceled Memberships';
        case 'newMemberships': return 'New Memberships';
        default:
            const result = key.replace(/([A-Z])/g, ' $1');
            return result.charAt(0).toUpperCase() + result.slice(1).trim();
    }
}

const calculateActivityMembershipTotals = (data: Record<string, MembershipTierData> | null) => {
    if (!data) return { monthlyQty: 0, monthlyRev: 0, yearlyQty: 0, yearlyRev: 0, };
    return Object.values(data).reduce((totals, item) => {
        totals.monthlyQty += item.monthly?.qty || 0;
        totals.monthlyRev += item.monthly?.revenue || 0;
        totals.yearlyQty += item.yearly?.qty || 0;
        totals.yearlyRev += item.yearly?.revenue || 0;
        return totals;
    }, { monthlyQty: 0, monthlyRev: 0, yearlyQty: 0, yearlyRev: 0, });
};

const calculateSimpleTableTotal = (data: Record<string, SimpleTotalData> | null) => {
    if (!data) return 0;
    return Object.values(data).reduce((total, item) => total + (item.total || 0), 0);
};

const membershipSortOrder = [
    'freeTrials',
    'starterPlus',
    'basic',
    'pro',
    'proPlus'
];

const sortMembershipRows = (a: [string, any], b: [string, any]): number => {
    const keyA = a[0];
    const keyB = b[0];

    const indexA = membershipSortOrder.indexOf(keyA);
    const indexB = membershipSortOrder.indexOf(keyB);

    if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
    }
    if (indexA !== -1) {
        return -1;
    }
    if (indexB !== -1) {
        return 1;
    }
    return formatKeyToLabel(keyA).localeCompare(formatKeyToLabel(keyB));
};

interface ReportTableProps {
    title: string;
    data: any | null;
    loading: boolean;
    error: string | null;
    columns: ReportTableColumn[];
    dataEntries: [string, any][];
    totalsRow: React.ReactNode;
    noDataMessage: string;
    sortFunction?: (a: [string, any], b: [string, any]) => number;
}

const ReportTable: React.FC<ReportTableProps> = ({
    title,
    data,
    loading,
    error,
    columns,
    dataEntries,
    totalsRow,
    noDataMessage,
    sortFunction,
}) => {
    const showTable = !loading && !error && data && dataEntries.length > 0;
    const showNoData = !loading && !error && (!data || dataEntries.length === 0);

    const defaultSort = (a: [string, any], b: [string, any]) => formatKeyToLabel(a[0]).localeCompare(formatKeyToLabel(b[0]));

    const currentSortFunction = sortFunction || defaultSort;

    return (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: 'white', display: 'flex', flexDirection: 'column', mb: 2, height: 'auto', }}>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '150px', }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ my: 2, flexShrink: 0, }}>{error}</Alert>}
            {showTable && (
                <TableContainer sx={{ flexGrow: 1, overflowY: 'auto', }}>
                    <Table size="small" aria-label={title} stickyHeader>
                        <TableHead sx={{ backgroundColor: 'grey.100', }}>
                            <TableRow>
                                {columns.map((col, index) => (
                                    <TableCell key={index} align={col.align ?? 'left'} sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', }}>
                                        {col.header}
                                        {col.headerSubtext && <Typography variant="caption" display="block" color="textSecondary">{col.headerSubtext}</Typography>}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dataEntries.sort(currentSortFunction).map(([key, value]) => (
                                <TableRow key={key} sx={{ '&:last-child td, &:last-child th': { border: 0, }, }}>
                                    {columns.map((col, index) => (
                                        <TableCell key={index} align={col.align ?? 'left'} component={index === 0 ? 'th' : 'td'} scope={index === 0 ? 'row' : undefined}>
                                            {col.render(key, value)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {totalsRow}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {showNoData && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '100px', }}>
                    <Typography variant="body2" color="text.secondary">{noDataMessage}</Typography>
                </Box>
            )}
        </Paper>
    );
};

interface MembershipStyleTableProps {
    title: string;
    data: MembershipReportData | Record<string, MembershipTierData> | null;
    loading: boolean;
    error: string | null;
    noDataMessage: string;
}

const MembershipStyleTable: React.FC<MembershipStyleTableProps> = ({ title, data, loading, error, noDataMessage, }) => {
    const totals = calculateActivityMembershipTotals(data);
    const dataEntries = data ? Object.entries(data) : [];
    const columns: ReportTableColumn[] = [
        { header: 'Description', render: (key: string, _value: MembershipTierData) => formatKeyToLabel(key), },
        { header: 'Qty.', align: 'right' as const, render: (_key: string, value: MembershipTierData) => value.monthly?.qty?.toLocaleString() ?? '0', },
        { header: 'Monthly', headerSubtext: '($)', align: 'right' as const, render: (_key: string, value: MembershipTierData) => value.monthly?.revenue ? formatCurrency(value.monthly.revenue) : formatCurrency(0), },
        { header: 'Qty.', align: 'right' as const, render: (_key: string, value: MembershipTierData) => value.yearly?.qty?.toLocaleString() ?? '0', },
        { header: 'Yearly', headerSubtext: '($)', align: 'right' as const, render: (_key: string, value: MembershipTierData) => value.yearly?.revenue ? formatCurrency(value.yearly.revenue) : formatCurrency(0), },
    ];
    const totalsRow = (
        <TableRow sx={{ '& > *': { fontWeight: 'bold', borderTop: '2px solid rgba(224, 224, 224, 1)', }, }}>
            <TableCell>Total</TableCell>
            <TableCell align="right">{totals.monthlyQty.toLocaleString()}</TableCell>
            <TableCell align="right">{formatCurrency(totals.monthlyRev)}</TableCell>
            <TableCell align="right">{totals.yearlyQty.toLocaleString()}</TableCell>
            <TableCell align="right">{formatCurrency(totals.yearlyRev)}</TableCell>
        </TableRow>
    );

    return (
        <ReportTable
            title={title}
            data={data}
            loading={loading}
            error={error}
            columns={columns}
            dataEntries={dataEntries}
            totalsRow={totalsRow}
            noDataMessage={noDataMessage}
            sortFunction={sortMembershipRows}
        />
    );
}

interface SimpleTableProps {
    title: string;
    data: Record<string, SimpleTotalData> | null;
    loading: boolean;
    error: string | null;
    noDataMessage: string;
}

const SimpleTable: React.FC<SimpleTableProps> = ({ title, data, loading, error, noDataMessage, }) => {
    const total = calculateSimpleTableTotal(data);
    const dataEntries = data ? Object.entries(data) : [];
    const columns: ReportTableColumn[] = [
        { header: 'Description', render: (key: string, _value: any) => formatKeyToLabel(key), },
        { header: 'Total ($)', align: 'right' as const, render: (_key: string, value: SimpleTotalData) => value.total ? formatCurrency(value.total) : formatCurrency(0), },
    ];
    const totalsRow = (
        <TableRow sx={{ '& > *': { fontWeight: 'bold', borderTop: '2px solid rgba(224, 224, 224, 1)', }, }}>
            <TableCell>Total</TableCell>
            <TableCell align="right"><strong>{formatCurrency(total)}</strong></TableCell>
        </TableRow>
    );

     return (
        <ReportTable
            title={title}
            data={data}
            loading={loading}
            error={error}
            columns={columns}
            dataEntries={dataEntries}
            totalsRow={totalsRow}
            noDataMessage={noDataMessage}
        />
    );
}

interface DateRangeSelectorProps {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    onStartDateChange: (date: Dayjs | null) => void;
    onEndDateChange: (date: Dayjs | null) => void;
    onSubmit: () => void;
    loading: boolean;
    error: string | null;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    onSubmit,
    loading,
    error,
}) => {
    return (
         <Paper elevation={0} sx={{ p: 2.5, mb: 4, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: 'white', }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, }}>
                <Typography variant="h6" sx={{ fontWeight: 'medium', }}>Activity Date Range</Typography>
                <Tooltip title="Select a date range to view New Memberships, Canceled Memberships, One-time Purchases, and Refunds that occurred within that period. Activity is also filtered by the selected Trading House." arrow placement="top">
                    <IconButton size="small" color="primary">
                        <InfoIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5} md={4}>
                        <DatePicker label="Start Date" value={startDate} onChange={onStartDateChange} slotProps={{ textField: { fullWidth: true, size: 'small', }, }} maxDate={endDate ?? undefined} />
                    </Grid>
                    <Grid item xs={12} sm={5} md={4}>
                        <DatePicker label="End Date" value={endDate} onChange={onEndDateChange} slotProps={{ textField: { fullWidth: true, size: 'small', }, }} minDate={startDate ?? undefined} />
                    </Grid>
                    <Grid item xs={12} sm={2} md={4}>
                        <Button variant="contained" color="primary" onClick={onSubmit} disabled={!startDate || !endDate || loading} fullWidth size="medium" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null} sx={{ textTransform: 'none', fontWeight: 'medium', height: '40px', }}>
                            {loading ? '...' : 'Get Activity'}
                        </Button>
                    </Grid>
                </Grid>
            </LocalizationProvider>
            {error && <Alert severity="warning" sx={{ mt: 2, }}>{error}</Alert>}
        </Paper>
    );
}

interface TradingHouseSelectorProps {
    selectedTradingHouse: string;
    onTradingHouseChange: (tradingHouse: string) => void;
    loading: boolean;
}

const TradingHouseSelector: React.FC<TradingHouseSelectorProps> = ({
    selectedTradingHouse,
    onTradingHouseChange,
    loading,
}) => {
    const handleChange = (event: SelectChangeEvent) => {
        onTradingHouseChange(event.target.value as string);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, }}>
            <Typography variant="body1" sx={{ mr: 1.5, fontWeight: 'medium' }}>Filter by:</Typography>
            <FormControl size="small" sx={{ minWidth: 150, }}>
                 <InputLabel id="trading-house-select-label">Trading House</InputLabel>
                <Select
                    labelId="trading-house-select-label"
                    label="Trading House"
                    value={selectedTradingHouse}
                    onChange={handleChange}
                    disabled={loading}
                >
                    <MenuItem value=""><em>All</em></MenuItem>
                    <MenuItem value="greenchart">GreenChart</MenuItem>
                    <MenuItem value="gtf">GTF</MenuItem>
                </Select>
            </FormControl>
        </Box>
    );
};

export default function SalesActivityReport() {
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(7, 'day').startOf('day'));
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(1, 'day').endOf('day'));

    const [selectedTradingHouse, setSelectedTradingHouse] = useState<string>("");

    const [activityReportData, setActivityReportData] = useState<ActivityReportData | null>(null);
    const [activityLoading, setActivityLoading] = useState(false);
    const [activityError, setActivityError] = useState<string | null>(null);

    const [membershipReportData, setMembershipReportData] = useState<MembershipReportData | null>(null);
    const [payedByCompanyCount, setPayedByCompanyCount] = useState<number>(0);
    const [membershipLoading, setMembershipLoading] = useState(false);
    const [membershipError, setMembershipError] = useState<string | null>(null);

    const isFetchingData = activityLoading || membershipLoading;

    const fetchActivityData = useCallback(async (start: Dayjs | null, end: Dayjs | null, houseFilter: string) => {
        if (!start || !end || !start.isValid() || !end.isValid()) {
            setActivityError("Invalid date range selected.");
            setActivityReportData(null);
            return;
        }

        setActivityLoading(true);
        setActivityError(null);
        setActivityReportData(null);

        try {
            const params: { startDate: string; endDate: string; tradingHouse?: string } = {
                startDate: start.utc().toISOString(),
                endDate: end.utc().toISOString(),
            };
            if (houseFilter) {
                params.tradingHouse = houseFilter;
            }

            const result = await getSalesActivityReportFunc(params) as CallableResponse<ActivityReportResult>;

            if (result?.data?.data && typeof result.data.data === 'object') {
                setActivityReportData(result.data.data);
            } else {
                throw new Error("Activity data format is invalid.");
            }
        } catch (error: any) {
            const message = error.message || 'Failed to fetch sales activity.';
            const code = error.code ? ` (Code: ${error.code})` : '';
            setActivityError(`${message}${code}`);
            setActivityReportData(null);
        } finally {
            setActivityLoading(false);
        }
    }, []);

    const fetchMembershipData = useCallback(async (houseFilter: string) => {
        setMembershipLoading(true);
        setMembershipError(null);
        setMembershipReportData(null);
        setPayedByCompanyCount(0);

        try {
            const params = houseFilter ? { tradingHouse: houseFilter } : {};
            const result = await getCurrentMembershipReportFunc(params) as CallableResponse<MembershipReportResult>;

            if (result?.data && typeof result.data.data === 'object' && typeof result.data.payedByCompanyCount === 'number') {
                setMembershipReportData(result.data.data);
                setPayedByCompanyCount(result.data.payedByCompanyCount);
            } else {
                throw new Error("Membership data format is invalid.");
            }
        } catch (error: any) {
            const message = error.message || 'Failed to fetch membership data.';
            const code = error.code ? ` (Code: ${error.code})` : '';
            setMembershipError(`${message}${code}`);
            setMembershipReportData(null);
            setPayedByCompanyCount(0);
        } finally {
            setMembershipLoading(false);
        }
    }, []);

    useEffect(() => {
        const initialHouseFilter = "";
        setSelectedTradingHouse(initialHouseFilter);

        fetchMembershipData(initialHouseFilter);

        if (startDate && endDate && startDate.isValid() && endDate.isValid()) {
           fetchActivityData(startDate, endDate, initialHouseFilter);
        } else {
             setActivityError("Default date range is invalid. Please select dates.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchActivityData, fetchMembershipData]);

    const handleSubmitActivity = () => {
        fetchActivityData(startDate, endDate, selectedTradingHouse);
    };

    const handleTradingHouseChange = (tradingHouse: string) => {
        setSelectedTradingHouse(tradingHouse);

        fetchMembershipData(tradingHouse);

        if (startDate && endDate && startDate.isValid() && endDate.isValid()) {
             fetchActivityData(startDate, endDate, tradingHouse);
        } else {
        }
    };

    const handleStartDateChange = (date: Dayjs | null) => {
        setStartDate(date);
        if (activityError) setActivityError(null);
    };
    const handleEndDateChange = (date: Dayjs | null) => {
        setEndDate(date);
         if (activityError) setActivityError(null);
    };


    return (
        <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh', }}>
            <Grid container spacing={4}>

                <Grid item xs={12} md={5} lg={4}>
                     <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', }}>
                         Active Memberships
                     </Typography>
                     <Typography variant="body2" paragraph color="text.secondary" sx={{mb: 2, }}>
                         Current active members and revenue. Updates when Trading House filter changes. Not affected by date range.
                     </Typography>

                     <TradingHouseSelector
                         selectedTradingHouse={selectedTradingHouse}
                         onTradingHouseChange={handleTradingHouseChange}
                         loading={isFetchingData}
                     />

                     {membershipError && !membershipLoading && (
                         <Alert severity="warning" sx={{ mb: 2, }}>{membershipError}</Alert>
                     )}

                     <MembershipStyleTable
                        title="Active Memberships Report"
                        data={membershipReportData}
                        loading={membershipLoading}
                        error={null}
                        noDataMessage="No active membership data found."
                    />

                     {!membershipLoading && membershipReportData !== null && !membershipError && (
                         <Paper elevation={0} sx={{ mt: 2, p: 2, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: 'white' }}>
                            <Typography variant="overline" display="block" color="text.secondary">
                                Of Total Active Subscriptions
                            </Typography>
                             <Typography variant="body1" component="span" sx={{ fontWeight: 'medium' }}>
                                 Company Paid:
                             </Typography>
                             <Typography variant="h6" component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                                 {payedByCompanyCount.toLocaleString()}
                             </Typography>
                         </Paper>
                     )}
                </Grid>

                <Grid item xs={12} md={7} lg={8}>
                    <DateRangeSelector
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={handleStartDateChange}
                        onEndDateChange={handleEndDateChange}
                        onSubmit={handleSubmitActivity}
                        loading={activityLoading}
                        error={activityError}
                    />

                    {(activityReportData || activityLoading || activityError) && (
                        <Grid container spacing={3}>
                             <Grid item xs={12} lg={6}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>New Memberships</Typography>
                                <MembershipStyleTable
                                    title="New Memberships"
                                    data={activityReportData?.newMemberships ?? null}
                                    loading={activityLoading}
                                    error={null}
                                    noDataMessage="No new memberships."
                                />
                                <Box mt={3}>
                                     <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>One-time Purchases</Typography>
                                    <SimpleTable
                                        title="One-time Purchases"
                                        data={activityReportData?.oneTimePurchases ?? null}
                                        loading={activityLoading}
                                        error={null}
                                        noDataMessage="No one-time purchases."
                                    />
                                </Box>
                             </Grid>
                            <Grid item xs={12} lg={6}>
                                 <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>Canceled Memberships</Typography>
                                <MembershipStyleTable
                                    title="Canceled Memberships"
                                    data={activityReportData?.canceledMemberships ?? null}
                                    loading={activityLoading}
                                    error={null}
                                    noDataMessage="No canceled memberships."
                                />
                                <Box mt={3}>
                                     <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>Refunds</Typography>
                                    <SimpleTable
                                        title="Refunds"
                                        data={activityReportData?.refunds ?? null}
                                        loading={activityLoading}
                                        error={null}
                                        noDataMessage="No refunds."
                                    />
                                 </Box>
                             </Grid>
                        </Grid>
                    )}
                     {!activityReportData && !activityLoading && !activityError && (
                         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', color: 'text.secondary', mt: 3, p:2, border: '1px dashed grey', borderRadius: 1 }}>
                             <Typography>Select a date range and click 'Get Activity' to view reports for the selected Trading House.</Typography>
                         </Box>
                     )}
                </Grid>
            </Grid>
        </Box>
    );
}